import { html, nothing } from "lit";
import type { SessionArtifactDetail } from "../controllers/session-artifacts.ts";

export type ParsedDiffLine = {
  kind: "add" | "del" | "ctx" | "meta";
  text: string;
};

export function classifyDiffLine(line: string): ParsedDiffLine {
  if (line.startsWith("+++") || line.startsWith("---") || line.startsWith("@@")) {
    return { kind: "meta", text: line };
  }
  if (line.startsWith("+")) {
    return { kind: "add", text: line };
  }
  if (line.startsWith("-")) {
    return { kind: "del", text: line };
  }
  return { kind: "ctx", text: line };
}

export function parseUnifiedDiffLines(diff: string): ParsedDiffLine[] {
  if (!diff.trim()) {
    return [];
  }
  return diff.split("\n").map((line) => classifyDiffLine(line));
}

export function renderHighlightedDiffLine(line: ParsedDiffLine) {
  return html`<div class="chat-diff__line chat-diff__line--${line.kind}">${line.text}</div>`;
}

export function renderHighlightedDiff(diff: string) {
  const lines = parseUnifiedDiffLines(diff);
  if (lines.length === 0) {
    return html`
      <p class="muted">No diff available.</p>
    `;
  }
  return html`
    <div class="chat-diff" role="region" aria-label="File diff">
      ${lines.map((line) => renderHighlightedDiffLine(line))}
    </div>
  `;
}

export function renderFullFileContent(content: string | null, label: string) {
  if (content == null || content === "") {
    return html`<p class="muted">No ${label} content.</p>`;
  }
  return html`<pre class="chat-diff__file mono">${content}</pre>`;
}

export type ChatFileDiffPanelProps = {
  detail: SessionArtifactDetail | null;
  loading?: boolean;
  error?: string | null;
  view: "diff" | "file";
  splitView?: boolean;
  showReviewActions?: boolean;
  reviewBusy?: boolean;
  onViewChange: (view: "diff" | "file") => void;
  onSplitViewChange?: (split: boolean) => void;
  onClose: () => void;
  onOpenInProjectFiles?: (path: string, artifactSessionKey?: string) => void;
  onAccept?: () => void;
  onReject?: () => void;
};

export function renderChatFileDiffPanel(props: ChatFileDiffPanelProps) {
  const change = props.detail?.change;
  const path = change?.path ?? "File";
  const op = change?.op ?? "modified";
  const fileContent =
    props.detail && op === "deleted" ? props.detail.before : (props.detail?.after ?? null);

  const diffBody = props.detail ? renderHighlightedDiff(props.detail.diff) : nothing;
  const fileBody = props.detail
    ? renderFullFileContent(fileContent, op === "deleted" ? "before" : "after")
    : nothing;

  return html`
    <div class="chat-file-diff-panel" role="dialog" aria-label="File change">
      <div class="chat-file-diff-panel__header">
        <div>
          <div class="chat-file-diff-panel__title mono">${path}</div>
          <div class="chat-file-diff-panel__sub muted">${op}</div>
        </div>
        <div class="chat-file-diff-panel__header-actions">
          ${
            props.onOpenInProjectFiles && path
              ? html`
                <button
                  type="button"
                  class="btn subtle btn--sm"
                  @click=${() =>
                    props.onOpenInProjectFiles?.(path, change?.sessionKey ?? undefined)}
                >
                  Open in files
                </button>
              `
              : nothing
          }
          <button type="button" class="btn subtle" @click=${props.onClose}>Close</button>
        </div>
      </div>
      <div class="chat-file-diff-panel__tabs" role="tablist">
        <button
          type="button"
          class="chat-file-diff-panel__tab ${props.view === "diff" ? "chat-file-diff-panel__tab--active" : ""}"
          role="tab"
          aria-selected=${props.view === "diff"}
          @click=${() => props.onViewChange("diff")}
        >
          Diff
        </button>
        <button
          type="button"
          class="chat-file-diff-panel__tab ${props.view === "file" ? "chat-file-diff-panel__tab--active" : ""}"
          role="tab"
          aria-selected=${props.view === "file"}
          @click=${() => props.onViewChange("file")}
        >
          File
        </button>
        ${
          props.onSplitViewChange
            ? html`
              <button
                type="button"
                class="chat-file-diff-panel__tab ${props.splitView ? "chat-file-diff-panel__tab--active" : ""}"
                @click=${() => props.onSplitViewChange?.(!props.splitView)}
              >
                Side by side
              </button>
            `
            : nothing
        }
      </div>
      ${
        props.showReviewActions && (props.onAccept || props.onReject)
          ? html`
            <div class="chat-file-diff-panel__review">
              <button
                type="button"
                class="btn primary btn--sm"
                ?disabled=${props.reviewBusy}
                @click=${() => props.onAccept?.()}
              >
                Accept
              </button>
              <button
                type="button"
                class="btn subtle btn--sm"
                ?disabled=${props.reviewBusy}
                @click=${() => props.onReject?.()}
              >
                Reject
              </button>
            </div>
          `
          : nothing
      }
      ${
        props.error
          ? html`<div class="callout danger">${props.error}</div>`
          : props.loading
            ? html`
                <p class="muted">Loading…</p>
              `
            : props.detail
              ? html`
                <div
                  class="chat-file-diff-panel__body ${props.splitView ? "chat-file-diff-panel__body--split" : ""}"
                >
                  ${
                    props.splitView
                      ? html`
                        <div class="chat-file-diff-panel__pane">
                          <div class="chat-file-diff-panel__pane-label muted">Diff</div>
                          ${diffBody}
                        </div>
                        <div class="chat-file-diff-panel__pane">
                          <div class="chat-file-diff-panel__pane-label muted">File</div>
                          ${fileBody}
                        </div>
                      `
                      : props.view === "diff"
                        ? diffBody
                        : fileBody
                  }
                </div>
              `
              : html`
                <div class="chat-file-diff-panel__body">
                  <div class="callout danger">
                    Could not load file change.
                    ${props.error ? html`<br /><span class="muted">${props.error}</span>` : nothing}
                  </div>
                </div>
              `
      }
    </div>
  `;
}
